import { Button, Typography, makeStyles } from "@material-ui/core"
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome"
import { faPenToSquare, faCheck } from "@fortawesome/free-solid-svg-icons"
import { RelapsePreventionPlacesByOptionId } from "../../../hooks/useRelapsePreventionPlaces"
import {
  keyToIconDef,
  SelectedSection,
  Section,
  SelectedOption,
} from "../../../utils/safetyPlanUtils"
import { colors } from "../../../utils/theme"
import { buildRenderableSelectedOptions } from "./safetyPlanSectionDisplay"
import SafetyPlanSelectedOptionValue from "./SafetyPlanSelectedOptionValue"

interface SafetyPlanSectionProps {
  selected: SelectedSection
  options: Map<string, Section>
  relapsePreventionPlacesByOptionId: RelapsePreventionPlacesByOptionId
  readOnly: boolean
  showSectionDescription: boolean
  handleOpenEdit: (value: string) => void
}

/**
 * Renders a single read-only safety plan section card as seen by the
 * practitioner, along with an Edit button to open the `EditSafetyPlan` modal
 * for that section.
 *
 * Each card displays:
 * - A colour-coded icon (derived from the section header via `getSectionIcon`)
 *   and the section title.
 * - A green checkmark badge indicating the section has been completed by the
 *   client.
 * - The section's description text.
 * - The client's selected response values, rendered via
 *   `SafetyPlanSelectedOptionValue`.
 *
 * Special-casing: for the "Keeping Myself Safe" section, `<br>` tags are
 * stripped from the description, and if no radio option is present in the
 * client's selections a placeholder radio entry is injected so the Yes/No
 * state is always visible.
 *
 * @param selected - The client's saved state for this section: contains the
 *   section id, header, description, and a `selectedOptionsMap` of only the
 *   response options the client has chosen, keyed by option id. This is the
 *   subset of data that reflects what the client actually entered.
 * @param options - The complete template definition for all sections, keyed by
 *   section id. Used to look up the full set of available response options and
 *   section metadata (e.g. icon, category types) independently of what the
 *   client has selected. The component reads from this map to determine things
 *   like which icon to display and whether the section contains a radio type.
 * @param handleOpenEdit - Callback invoked with the section id when the Edit
 *   button is clicked, triggering the `EditSafetyPlan` modal in the parent.
 * @param showSectionDescription - Specifies whether to show section descriptions. Currently the relapse prevention plan does not display these
 * @param readOnly - Specifies whether to allow editing
 * @param relapsePreventionPlacesByOptionId - Map of option ids (one for risky places, one for safe places) to saved places
 */
export default function SafetyPlanSection({
  selected,
  options,
  relapsePreventionPlacesByOptionId,
  readOnly,
  showSectionDescription,
  handleOpenEdit,
}: SafetyPlanSectionProps) {
  const classes = useStyles()
  let data = options.get(selected.id)
  if (!data) return null
  const optionsArr = buildRenderableSelectedOptions(
    selected,
    data,
    relapsePreventionPlacesByOptionId
  )

  // checks if an option is selected in Keeping Myself Safe Section
  const isKeepingMyselfSafe = optionsArr.some(
    (el: SelectedOption) => el.category_type === "radio"
  )
  // if a Keeping Myself Safe option is not selected, we add it to the options array to display the "Yes" or "No" buttons
  if (!isKeepingMyselfSafe && data.header.includes("Myself Safe")) {
    optionsArr.push({
      id: "keepingMyselfSafe",
      category_type: "radio",
      custom_values: [],
      response_text: "",
      display_order: 14,
    })
  }

  const selectedOptions = optionsArr.map((d: SelectedOption) => (
    <SafetyPlanSelectedOptionValue
      option={d}
      activeRelapsePreventionPlaces={
        relapsePreventionPlacesByOptionId.get(d.id) ?? []
      }
      key={d.id}
    />
  ))

  let descriptionText = selected.description
  if (selected.header.includes("Myself Safe")) {
    descriptionText = descriptionText.replace(/<br\s*\/?>/gi, "")
  }

  return (
    <>
      <div className={classes.sectionContainer}>
        <div className={classes.sectionHeader}>
          <div
            className={classes.iconContainer}
            style={{ background: selected.icon_color }}
          >
            <FontAwesomeIcon icon={keyToIconDef(selected.icon)} />
          </div>
          <Typography
            className={classes.sectionHeaderText}
            variant="h5"
            component="div"
            dangerouslySetInnerHTML={{ __html: selected.header }}
          />
          <div className={classes.sectionCheck}>
            <FontAwesomeIcon icon={faCheck} />
          </div>
        </div>
        <div className={classes.sectionContent}>
          {!readOnly && (
            <Button
              id={selected.id}
              className={classes.sectionEdit}
              onClick={() => handleOpenEdit(selected.id)}
            >
              <span>Edit</span>
              <FontAwesomeIcon
                icon={faPenToSquare}
                style={{ marginLeft: "4px" }}
              />
            </Button>
          )}
          {showSectionDescription && (
            <Typography
              className={classes.sectionContentText}
              component="div"
              dangerouslySetInnerHTML={{ __html: descriptionText }}
            />
          )}
          {selectedOptions.length > 0 ? (
            selectedOptions
          ) : (
            <div className={classes.emptyOptionsContainer}>
              <Typography className={classes.emptyOptionsText}>
                No responses
              </Typography>
            </div>
          )}
        </div>
      </div>
    </>
  )
}

const useStyles = makeStyles(() => ({
  sectionHeader: {
    display: "flex",
    padding: "12px 20px",
    alignItems: "center",
    gap: "10px",
    alignSelf: "stretch",
    borderBottom: `1px solid ${colors.safetyPlan.grayscale.black10}`,
    background: colors.neutral.white,
  },
  sectionHeaderText: {
    color: colors.safetyPlan.ink,
    lineHeight: "20px",
  },
  sectionCheck: {
    display: "flex",
    padding: "12px",
    justifyContent: "center",
    alignItems: "center",
    gap: "4px",
    borderRadius: "360px",
    background: colors.safetyPlan.lime,
    color: colors.neutral.white,
    marginLeft: "auto",
  },
  sectionContent: {
    display: "flex",
    padding: "16px 20px 24px 20px",
    flexDirection: "column",
    alignItems: "flex-start",
    gap: "12px",
    background: colors.safetyPlan.cardSecondaryBackground,
  },
  sectionContentText: {
    fontSize: "16px",
    fontStyle: "normal",
    fontWeight: 700,
    lineHeight: "20px",
    color: colors.neutral.asphalt,
  },
  sectionContainer: {
    marginBottom: "20px",
    borderRadius: "8px",
    overflow: "hidden",
  },
  sectionEdit: {
    padding: "4px 20px",
    fontSize: "16px",
    borderRadius: "200px",
    border: `2px solid ${colors.primary.shadow}`,
    color: colors.primary.shadow,
    background: colors.safetyPlan.grayscale.white85,
    textTransform: "none",
  },
  iconContainer: {
    display: "flex",
    width: "36px",
    height: "36px",
    minWidth: "36px",
    minHeight: "36px",
    padding: "6.923px 8.308px 9.692px 8.308px",
    justifyContent: "center",
    alignItems: "center",
    borderRadius: "360px",
    flexShrink: 0,
    color: "white",
  },
  emptyOptionsContainer: {
    width: "100%",
    padding: "14px 16px",
    borderRadius: "8px",
    border: `1px dashed ${colors.safetyPlan.grayscale.black10}`,
    background: colors.safetyPlan.grayscale.white85,
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
  },
  emptyOptionsText: {
    color: colors.neutral.asphalt,
    fontSize: "15px",
    fontWeight: 500,
    fontStyle: "italic",
    lineHeight: "20px",
    opacity: 0.75,
  },
}))
